import React, { useContext, useState } from 'react';
import { cloneDeep } from 'lodash';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { Box, Checkbox, TextField, Tooltip, Typography } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import DirectionsIcon from '@mui/icons-material/Directions';
import { assign } from 'xstate';
import { DatePicker, LocalizationProvider } from '@mui/lab';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import deLocale from 'date-fns/locale/de';
import { IntlShape } from 'react-intl';
import { makeStyles } from '@mui/styles';

import { MachineContext } from '../../MachineContext';
import { BaseComponent } from '../Component';
import { SendContext } from '../../SendContext';
import { translateWithFallback } from '../../util';
import { CardImageEnum, ObjectCardImage } from '../ObjectCard';
import { EvoachButton } from '../Button';
import { theme as evoachDefaultTheme } from '../../theme/theme';

import { MultipleOutputEnum } from './MultipleOutputEnum';

export interface MultipleOutputComponent extends BaseComponent {
  type: 'multipleOutput';
  props: MultipleOutputProps;
}

export interface MultipleOutputItem {
  type: string;
  value: string;
  starred?: boolean;
  date?: Date | null;
  /**
   * @deprecated The method should not be used
   */
  image?: CardImageEnum;
  imageSrc?: string;
}
export interface MultipleOutputProps {
  elements: MultipleOutputItem[][];
  disableDrag?: boolean;
  maxItems?: number;
  minItems?: number;
  selectAll?: boolean;
  hideDate?: boolean;
  hideSelect?: boolean;
  iconType?: MultipleOutputEnum;
  buttonText: string;
  saveResultTo: string;
  toolTipWarningMessage?: string;
}

//remove border from date input
const useStyles = makeStyles(() => ({
  noBorder: {
    borderRadius: '0',
    border: 'none',
  },
}));

// a little function to help us with reordering the result
const reorder = (
  list: any,
  startIndex: any,
  endIndex: any
): MultipleOutputItem[] => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result as MultipleOutputItem[];
};

const selectItems = (
  list: MultipleOutputItem[],
  selectAll: boolean | undefined
): MultipleOutputItem[] => {
  // select all
  if (selectAll) {
    list.forEach((value) => {
      value.starred = true;
    });
  }

  // make list distinct
  let templist: any = [];
  list.forEach((listentry: MultipleOutputItem) => {
    templist[listentry.value] = listentry;
  });

  list = Object.keys(templist).map((listkey: string) => templist[listkey]);
  return list;
};

const starredItems = (list: MultipleOutputItem[]): number => {
  let result = 0;
  list.forEach((value) => {
    if (value.starred) result++;
  });
  return result;
};

export const MultipleOutput: React.FC<MultipleOutputProps> = ({
  elements,
  disableDrag,
  maxItems,
  minItems,
  selectAll,
  hideDate = false,
  hideSelect = false,
  iconType,
  buttonText,
  saveResultTo,
  toolTipWarningMessage = '',
}) => {
  const { send } = useContext(SendContext);
  const [items, setItems] = useState<MultipleOutputItem[]>(
    elements ? selectItems(elements.flat(), selectAll) : []
  );
  const classes = useStyles();
  const [warning, setWarning] = useState<string>('');

  const [hoverIndex, setHoverIndex] = useState<number | undefined>(undefined);

  const hasNoIconOrObjectCard =
    hideSelect &&
    items &&
    items.length > 0 &&
    !items[0].image &&
    !items[0].imageSrc;

  const checkTooltip = (index: number) => {
    const newItems = cloneDeep(items);
    if (newItems[index].starred) {
      setWarning('');
    } else if (maxItems) {
      if (maxItems <= starredItems(items)) {
        setWarning(toolTipWarningMessage);
      } else {
        setWarning('');
      }
    }
  };

  const starItem = (index: number) => {
    const newItems = cloneDeep(items);
    newItems[index].starred = !newItems[index].starred;
    setItems(newItems);
  };

  const getItemSelectJSX = (item: MultipleOutputItem, index: number) => {
    return (
      <Box sx={{ paddingLeft: '10px', paddingRight: '5px' }}>
        <Checkbox
          sx={{
            width: '45px',
            visibility: hideSelect ? 'hidden' : 'visible',
            margin: '0px',
            '&:hover': {
              svg: {
                color: item.starred
                  ? evoachDefaultTheme.palette.secondary.dark
                  : evoachDefaultTheme.palette.grey.A700,
              },
            },
          }}
          checkedIcon={
            iconType === 'direction' ? (
              <DirectionsIcon
                sx={{
                  fontSize: '2.5rem',
                  color:
                    hoverIndex === index
                      ? evoachDefaultTheme.palette.secondary.dark
                      : evoachDefaultTheme.palette.secondary.main,
                }}
                color="secondary"
              />
            ) : (
              <StarIcon
                sx={{
                  fontSize: '2.5rem',
                  color:
                    hoverIndex === index
                      ? evoachDefaultTheme.palette.secondary.dark
                      : evoachDefaultTheme.palette.secondary.main,
                }}
                color="secondary"
              />
            )
          }
          icon={
            iconType === 'direction' ? (
              <DirectionsIcon
                sx={{
                  fontSize: '2.5rem',
                  color:
                    hoverIndex === index
                      ? evoachDefaultTheme.palette.grey.A700
                      : undefined,
                }}
              />
            ) : (
              <StarIcon
                sx={{
                  fontSize: '2.5rem',
                  color:
                    hoverIndex === index
                      ? evoachDefaultTheme.palette.grey.A700
                      : undefined,
                }}
              />
            )
          }
          checked={!!item.starred}
          onClick={() => starItem(index)}
        />
      </Box>
    );
  };

  return (
    <div>
      <DragDropContext
        onDragEnd={(result) => {
          // dropped outside the list
          if (!result.destination) {
            return;
          }

          const sortedItems = reorder(
            items,
            result.source.index,
            result.destination.index
          );

          setItems(sortedItems);
        }}
      >
        <Droppable droppableId="droppable">
          {(droppableProvided: any) => (
            <div ref={droppableProvided.innerRef}>
              {items.map((item, index) => {
                return (
                  <Draggable
                    key={`${item.type}-${item.value}-${index}`}
                    draggableId={`${item.type}-${item.value}-${index}`}
                    index={index}
                    isDragDisabled={disableDrag}
                  >
                    {(draggableProvided, draggableSnapshot) => (
                      <Tooltip
                        key={`${item.type}-${item.value}-${index}`}
                        title={warning == null ? '' : warning}
                        onMouseEnter={() => {
                          checkTooltip(index);
                        }}
                      >
                        <Box
                          onMouseOver={() => {
                            setHoverIndex(index);
                          }}
                          onMouseLeave={() => {
                            setHoverIndex(undefined);
                          }}
                          sx={[
                            {
                              display: 'flex',
                              flexDirection: 'row',
                              alignItems: 'center',
                              padding: '0',
                              userSelect: 'none',
                              margin: '0 0 1rem 0',
                              borderRadius: '30px',
                              border: 'solid 0.5px black',
                              background: 'white',
                            },
                          ]}
                          ref={draggableProvided.innerRef}
                          {...draggableProvided.draggableProps}
                          {...draggableProvided.dragHandleProps}
                        >
                          {!hideSelect ? (
                            getItemSelectJSX(item, index)
                          ) : item.image ? (
                            <Box sx={{ flexGrow: 0, paddingLeft: '10px' }}>
                              <ObjectCardImage
                                imageSrc={item.imageSrc}
                                imageSize={50}
                                width={70}
                              />
                            </Box>
                          ) : (
                            ''
                          )}
                          <Box
                            sx={[
                              {
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                flexGrow: 1,
                                justifyContent: 'space-beetween',
                                borderTopRightRadius: '30px',
                                borderBottomRightRadius: '30px',
                                borderTopLeftRadius: hasNoIconOrObjectCard
                                  ? '30px'
                                  : '0px',
                                borderBottomLeftRadius: hasNoIconOrObjectCard
                                  ? '30px'
                                  : '0px',
                                background:
                                  draggableSnapshot.isDragging ||
                                  (hoverIndex !== undefined &&
                                    hoverIndex === index)
                                    ? evoachDefaultTheme.palette.secondary.light
                                    : '#d1edf3',
                                cursor: 'pointer',
                              },
                            ]}
                            onClick={() => starItem(index)}
                          >
                            <Box flexGrow={1}>
                              <Typography
                                style={{
                                  paddingLeft: hasNoIconOrObjectCard
                                    ? '25px'
                                    : '10px',
                                  paddingRight: '8px',
                                  paddingTop: '17px',
                                  paddingBottom: '17px',
                                  minWidth: '100px',
                                }}
                              >
                                {item.value}
                              </Typography>
                            </Box>
                            {hideDate === false ? (
                              <Box
                                flexGrow={0}
                                sx={{
                                  maxWidth: '250px',
                                  minWidth: '150px',
                                  width: 0,
                                  visibility: hideDate ? 'hidden' : 'visible',
                                  marginRight: '0.5rem',
                                }}
                              >
                                <LocalizationProvider
                                  dateAdapter={AdapterDateFns}
                                  locale={deLocale}
                                >
                                  <DatePicker
                                    disabled={!item.starred && !hideSelect}
                                    disablePast
                                    value={item.date}
                                    inputFormat="dd.MM.yyyy"
                                    mask="__.__.____"
                                    onChange={(date) => {
                                      const newItems = cloneDeep(items);
                                      newItems[index].date = date;
                                      setItems(newItems);
                                    }}
                                    renderInput={(params) => (
                                      <TextField {...params} />
                                    )}
                                    InputProps={{
                                      classes: {
                                        notchedOutline: classes.noBorder,
                                      },
                                    }}
                                  />
                                </LocalizationProvider>
                              </Box>
                            ) : (
                              ''
                            )}
                          </Box>
                        </Box>
                      </Tooltip>
                    )}
                  </Draggable>
                );
              })}
              {droppableProvided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      <EvoachButton
        disabled={
          !hideSelect &&
          ((minItems !== undefined && minItems > starredItems(items)) ||
            (maxItems !== undefined && maxItems < starredItems(items)))
        }
        onClick={() => {
          send({
            type: 'writeToContext',
            payload: {
              [saveResultTo]: items.filter(
                (item) => item.starred || hideSelect
              ),
            },
          });
          send({
            type: 'writeToContext',
            payload: {
              [saveResultTo + '_NOTSELECTED']: items.filter(
                (item) => !(item.starred || hideSelect)
              ),
            },
          });
          send({ type: 'next' });
        }}
      >
        {buttonText}
      </EvoachButton>
    </div>
  );
};

export const renderMultipleOutputAction = (intl: IntlShape) => {
  return assign((context: MachineContext, _event, actionMetadata) => {
    const newContext = cloneDeep(context);
    newContext.widgetData = newContext.widgetData.filter(
      (element) => !element.temporary
    );

    const payload = actionMetadata.action.payload;
    const hideDate =
      payload.hideDate &&
      payload.hideDate !== undefined &&
      payload.hideDate !== null
        ? payload.hideDate
        : false;
    const hideSelect =
      payload.hideSelect &&
      payload.hideSelect !== undefined &&
      payload.hideSelect !== null
        ? payload.hideSelect
        : false;

    const dataSourceList = payload.getValuesFrom;

    // if getValuesFrom is set, then use it to retrieve elements
    // if not, retrieve elements directly from payload
    const elements =
      dataSourceList && Array.isArray(dataSourceList)
        ? dataSourceList
            .filter(
              (datasource: string) =>
                datasource !== undefined &&
                datasource !== null &&
                datasource.trim() !== '' &&
                context.userData[datasource] !== undefined
            )
            .map((dataSource: string) => {
              const originalData = context.userData[dataSource];

              // ! if adapt anything here, consider adapting it in
              // ! MultipleOutputComposite.tsx, too
              return originalData.map((entry: any) => {
                if (typeof entry === 'string') {
                  return {
                    type: 'undefined',
                    value: entry,
                  };
                } else {
                  // added array handling with PROD-1623
                  if (Array.isArray(entry)) {
                    if (typeof entry[0] === 'string') {
                      return entry.map((singleEntry: string) => {
                        return {
                          type: 'undefined',
                          value: singleEntry,
                        };
                      });
                    } else {
                      return entry;
                    }
                  } else {
                    return {
                      ...entry,
                      date: entry.date ?? null,
                      starred: false, // PROD-1277, prev.: !!entry.starred,
                    };
                  }
                }
              });
            })
            .flat() // PROD-1623
        : payload.elements;

    newContext.widgetData.push({
      type: 'multipleOutput',
      temporary: actionMetadata.action.temporary ?? false,
      props: {
        elements: elements,
        saveResultTo: payload.saveResultTo,
        buttonText: translateWithFallback(intl, payload.buttonText),
        disableDrag: payload.disableDrag,
        maxItems: payload.maxItems,
        minItems: payload.minItems,
        selectAll: payload.selectAll,
        hideDate: hideDate,
        hideSelect: hideSelect,
        iconType: payload.iconType,
        toolTipWarningMessage: translateWithFallback(
          intl,
          payload.toolTipWarningMessage
        ),
      },
    });
    return newContext;
  });
};
